import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { API, getInitials } from "@/lib/shared";

export const PublicIntakeForm = () => {
  const { patientId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [patient, setPatient] = useState(null);
  const [appt, setAppt] = useState(null);
  const [service, setService] = useState(null);
  const [form, setForm] = useState({
    date_of_birth: '', gender: '', phone: '', email: '',
    address: '', city: '', state: '', zip_code: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    allergies: '', current_medications: '', medical_conditions: '', past_surgeries: '',
    prior_iv_therapy: false, prior_iv_notes: '', hydration_concerns: '',
    hipaa_acknowledged: false, treatment_consent: false, notes: '',
  });

  useEffect(() => {
    if (!patientId || !token) { setError('Invalid intake link.'); setLoading(false); return; }
    axios.get(`${API}/public/intake/${patientId}?token=${encodeURIComponent(token)}`)
      .then(res => {
        setPatient(res.data.patient || null);
        setAppt(res.data.upcoming_appointment || null);
        setService(res.data.service || null);
        // Prefill any existing values
        const p = res.data.patient || {};
        setForm(f => ({
          ...f,
          date_of_birth: p.date_of_birth || '',
          gender: p.gender || '',
          phone: p.phone || '',
          email: p.email || '',
          address: p.address || '',
          city: p.city || '',
          state: p.state || '',
          zip_code: p.zip_code || '',
          emergency_contact_name: p.emergency_contact_name || '',
          emergency_contact_phone: p.emergency_contact_phone || '',
          emergency_contact_relationship: p.emergency_contact_relationship || '',
          allergies: p.allergies || '',
          current_medications: p.current_medications || '',
          medical_conditions: p.medical_conditions || '',
          past_surgeries: p.past_surgeries || '',
          prior_iv_therapy: !!p.prior_iv_therapy,
          prior_iv_notes: p.prior_iv_notes || '',
          hydration_concerns: p.hydration_concerns || '',
          hipaa_acknowledged: !!p.hipaa_acknowledged,
          treatment_consent: !!p.treatment_consent,
          notes: p.notes || '',
        }));
      })
      .catch(err => setError(err.response?.data?.detail || 'Link expired or invalid.'))
      .finally(() => setLoading(false));
  }, [patientId, token]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.hipaa_acknowledged || !form.treatment_consent) {
      toast.error('Please review and acknowledge both consents to submit.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/public/intake/${patientId}?token=${encodeURIComponent(token)}`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-primary">Loading your intake form…</p></div>;

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md gold-border">
        <CardContent className="p-8 text-center space-y-3">
          <X className="w-12 h-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">Invalid Link</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground">Please contact EliteIV to receive a new intake form link.</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md gold-border">
        <CardContent className="p-8 text-center space-y-4">
          <CheckCircle2 className="w-16 h-16 mx-auto text-primary" />
          <h2 className="text-2xl brand-heading gold-gradient-text">Thank you, {patient?.first_name}!</h2>
          <p className="text-sm text-muted-foreground">Your intake form has been submitted. Your private nurse will review it before your appointment.</p>
          {appt && service && <p className="text-xs text-primary mt-3">Looking forward to your <strong>{service.name}</strong> session on <strong>{format(new Date(appt.start_datetime), 'EEEE, MMM d · h:mm a')}</strong>.</p>}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-gradient-to-r from-black via-zinc-950 to-black border-b border-primary/30">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl brand-heading"><span className="gold-gradient-text">Elite</span><span className="text-muted-foreground">IV</span></h1>
            <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-1">Patient Intake</p>
          </div>
          {patient && <Avatar className="h-12 w-12 ring-2 ring-primary/40"><AvatarFallback className="bg-primary/15 text-primary">{getInitials(patient.first_name, patient.last_name)}</AvatarFallback></Avatar>}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <Card className="gold-border bg-card/60">
          <CardContent className="p-6 space-y-1">
            <p className="text-xs uppercase tracking-widest text-primary/70">Welcome</p>
            <h2 className="text-3xl brand-heading"><span className="gold-gradient-text">Hi {patient?.first_name},</span></h2>
            <p className="text-sm text-muted-foreground">Please complete this form so your private EliteIV nurse can prepare for your mobile session.</p>
            {appt && service && (
              <div className="mt-4 p-3 bg-primary/5 border border-primary/30 rounded-md text-sm">
                <span className="text-primary font-semibold">Your upcoming visit: </span>{service.name} · {format(new Date(appt.start_datetime), 'EEE, MMM d · h:mm a')}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Demographics */}
        <Card className="bg-card/60">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-primary border-b border-primary/20 pb-2">Demographics</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} /></div>
              <div><Label className="text-xs">Gender</Label>
                <Select value={form.gender} onValueChange={(v) => set('gender', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Non-binary">Non-binary</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Mobile Phone</Label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+15551234567" data-testid="intake-phone" /><p className="text-[10px] text-muted-foreground mt-1 leading-snug" data-testid="intake-sms-optin">By providing your mobile number you agree to receive appointment confirmations and reminders by text from EliteIV Lounge. Msg &amp; data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.</p></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs">Home Address</Label><Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="123 Main St, Apt 4B" /></div>
              <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">State</Label><Input value={form.state} onChange={(e) => set('state', e.target.value)} /></div>
                <div><Label className="text-xs">Zip</Label><Input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} /></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="bg-card/60">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-primary border-b border-primary/20 pb-2">Emergency Contact</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Name</Label><Input value={form.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} /></div>
              <div><Label className="text-xs">Relationship</Label><Input value={form.emergency_contact_relationship} onChange={(e) => set('emergency_contact_relationship', e.target.value)} placeholder="Spouse, Parent…" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Medical History */}
        <Card className="bg-card/60">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-primary border-b border-primary/20 pb-2">Medical History</h3>
            <div><Label className="text-xs text-destructive">⚠ Allergies (medications, foods, latex, etc.)</Label><Textarea rows={2} value={form.allergies} onChange={(e) => set('allergies', e.target.value)} placeholder="List any known allergies..." /></div>
            <div><Label className="text-xs">Current Medications & Supplements</Label><Textarea rows={2} value={form.current_medications} onChange={(e) => set('current_medications', e.target.value)} placeholder="List all current prescriptions, OTCs, vitamins..." /></div>
            <div><Label className="text-xs">Medical Conditions</Label><Textarea rows={2} value={form.medical_conditions} onChange={(e) => set('medical_conditions', e.target.value)} placeholder="Diabetes, heart conditions, etc." /></div>
            <div><Label className="text-xs">Past Surgeries</Label><Textarea rows={2} value={form.past_surgeries} onChange={(e) => set('past_surgeries', e.target.value)} /></div>
            <div className="flex items-center gap-3 p-3 bg-muted/20 border border-border/40 rounded-md">
              <Switch checked={form.prior_iv_therapy} onCheckedChange={(v) => set('prior_iv_therapy', v)} />
              <Label className="text-sm flex-1">I have received IV therapy before</Label>
            </div>
            {form.prior_iv_therapy && (
              <div><Label className="text-xs">Notes about prior IV therapy</Label><Textarea rows={2} value={form.prior_iv_notes} onChange={(e) => set('prior_iv_notes', e.target.value)} placeholder="What did you have? Any reactions?" /></div>
            )}
            <div><Label className="text-xs">What are you hoping to get out of today's session?</Label><Textarea rows={2} value={form.hydration_concerns} onChange={(e) => set('hydration_concerns', e.target.value)} placeholder="Energy boost, post-workout recovery, hangover relief…" /></div>
          </CardContent>
        </Card>

        {/* Consent */}
        <Card className="bg-card/60">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-primary border-b border-primary/20 pb-2">Consent & Privacy</h3>
            <div className="flex items-start gap-3 p-3 bg-muted/20 border border-border/40 rounded-md">
              <Checkbox checked={form.hipaa_acknowledged} onCheckedChange={(v) => set('hipaa_acknowledged', !!v)} data-testid="intake-hipaa" className="mt-0.5" />
              <Label className="text-sm leading-relaxed">
                <strong className="text-primary">HIPAA Privacy Acknowledgment.</strong> I acknowledge that EliteIV will protect my health information in accordance with HIPAA. I consent to electronic communication (email/SMS) for appointment reminders.
              </Label>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/20 border border-border/40 rounded-md">
              <Checkbox checked={form.treatment_consent} onCheckedChange={(v) => set('treatment_consent', !!v)} data-testid="intake-treatment" className="mt-0.5" />
              <Label className="text-sm leading-relaxed">
                <strong className="text-primary">Consent to Treatment.</strong> I voluntarily consent to receive IV hydration and/or vitamin/injection therapy as ordered. I understand the procedures, possible risks (bruising, infection, allergic reaction), and benefits. I confirm the information I've provided is accurate.
              </Label>
            </div>
            <div><Label className="text-xs">Anything else we should know? (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
          </CardContent>
        </Card>

        <Button onClick={submit} disabled={submitting || !form.hipaa_acknowledged || !form.treatment_consent} data-testid="intake-submit" className="w-full h-12 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black font-semibold tracking-widest text-sm">
          {submitting ? 'SUBMITTING…' : 'SUBMIT INTAKE FORM'}
        </Button>
        <p className="text-[10px] text-center text-muted-foreground">Your information is encrypted and only visible to your EliteIV care team.</p>
      </main>
    </div>
  );
};
